// 發布世界觀
import { useState, useRef } from "react";
import { useSelector } from "react-redux";
import Axios from '@/shared/api/axios';
import MessageModal from '@/components/ui/MessageModal';
import StatusModal from '@/components/ui/StatusModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { useUnmountSafeTimeout } from '@/hooks/useUnmountSafeTimeout';
import MainNav from '@/components/layout/AppNavbar';
import PostMediaEditor from '@/features/posts/components/PostMediaEditor';
import EditMediaModal from '@/features/posts/components/EditMediaModal';
import MediaToolbar from '@/features/posts/components/MediaToolbar';

import placeholder_portrait_40x40 from "@/assets/imgs/placeholders/portrait_40x40.png";
import SafeImg from '@/components/shared/SafeImg';

import { processImage } from '@/shared/utils/imageProcessor';
import { extractTags, stripTags } from '@/shared/utils/tags';
import { pastePlainText, pastePlainTextSingleLine, blockNewline, toSingleLine } from '@/shared/utils/contentEditableInput';
import { WORLD_IMAGE_SIZES } from '@/shared/constants/mediaSizes';

// 世界標題上限，對應後端 VARCHAR(100)；用 code-point 計算以與 DB 字元數一致
const MAX_WORLD_TITLE = 100;

const MyWorldPost = () => {
  const navigate = useNavigate();
  // 成功後延遲導頁用：期間若已離開頁面，卸載時清掉 timer，不會被拉回來
  const setRedirectTimeout = useUnmountSafeTimeout();
  const { t } = useTranslation();

  const idolId = useSelector((state) => state.user.idolId);
  const avatarUrl = useSelector((state) => state.user.avatarUrl);

  // API 非成功狀態碼統一由 StatusModal 顯示（含攔截器附加的 HTTP_xxx）
  const statusModalRef = useRef(null);
  const [statusCode, setStatusCode] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [imageCaption, setImageCaption] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [titleError, setTitleError] = useState(false);

  const modalPostSuccess = useRef(null);
  const modalSize = useRef(null);
  const modalType = useRef(null);
  const modalImageProcessError = useRef(null);
  const editMediaModalRef = useRef(null);

  const titleRef = useRef(null);
  const contentEditableRef = useRef(null);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    // 立即清 value，讓使用者能再選相同檔案（例如刪掉後重新加回）
    event.target.value = '';
    if (!file) return;

    const isImage = file.type.startsWith('image/');

    // 檢查檔案大小
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      modalSize.current.show();
      return;
    }

    if (!isImage) {
      modalType.current.show();
      return;
    }

    // 圖片格式檢查
    if (file.type !== "image/jpg" &&
      file.type !== "image/jpeg" &&
      file.type !== "image/png") {
      modalType.current.show();
      return;
    }

    try {
      const results = await processImage(file, {
        sizes: WORLD_IMAGE_SIZES,
      });

      // 世界只能有一張圖片，直接替換
      setImages([{
        type: 0, // 0 = image
        file: results.small,
        big_file: results.big,
        url: URL.createObjectURL(results.small),
        big_url: URL.createObjectURL(results.big),
      }]);
    } catch (error) {
      // 圖片讀取／解碼失敗（例如檔案損毀）
      console.error("Error processing image:", error);
      modalImageProcessError.current.show();
    }
  };

  const handleDeleteImage = () => {
    setImages([]);
    setImageCaption('');
  };

  const handleEditMedia = (index) => {
    const media = images[index];
    editMediaModalRef.current.show({ ...media, index }, imageCaption);
  };

  const createPost = async () => {
    // 檢查標題字數
    if ([...title.trim()].length > MAX_WORLD_TITLE) {
      setTitleError(true);
      return;
    }
    setTitleError(false);

    // 檢查內容是否為空
    if (!canPost) {
      return;
    }

    setIsLoading(true);

    const currentContent = content.trim();
    const filterTags = extractTags(currentContent);
    const contentWithoutTags = stripTags(currentContent).replace(/\s+$/, '');

    const formData = new FormData();
    formData.append('idol_id', idolId);
    formData.append('content', contentWithoutTags);
    formData.append('title', title);

    // 處理圖片
    if (images.length > 0) {
      const image = images[0];
      formData.append('world_file', image.file);
      if (image.big_file) {
        formData.append('big_world_file', image.big_file);
      }
      // 添加圖片說明
      if (imageCaption) {
        formData.append('world_file_caption', imageCaption);
      }
    }

    // tags 陣列處理
    filterTags.forEach((tag, index) => {
      formData.append(`tags[${index}]`, tag);
    });

    try {
      const res = await Axios.post("/world/create", formData);

      if (res.data.status_code === 'SYSTEM_1000') {
        modalPostSuccess.current.show();
        setRedirectTimeout(() => navigate(`/world-view/${res.data.world_uuid}`), 2000);
      } else {
        setStatusCode(res.data.status_code);
        statusModalRef.current?.show();
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error("Error creating world:", error);
      }
    }
    setIsLoading(false);
  };

  const handleTitleInput = () => {
    if (titleRef.current) {
      // 標題是單行欄位，state 直接存正規化後的值：長度驗證、canPost、送出三者
      // 才會是同一個值。toSingleLine 收掉 onPaste / onKeyDown 攔不到的換行入口
      // （拖曳、行動裝置軟鍵盤的換行鍵、語音聽寫）
      setTitle(toSingleLine(titleRef.current.innerText).trim());
    }
  };

  const handleContentInput = () => {
    if (contentEditableRef.current) {
      const newContent = contentEditableRef.current.innerText.trim();
      setContent(newContent);
    }
  };

  // 檢查是否能發布（標題和內容都必填）
  const canPost = title.trim().length > 0 && content.trim().length > 0;



  return (
    <>
      <MainNav />
      <main className="container-lg nav-padding">
        <div className="mt-4 border rounded shadowA bg-white">
          <div className="text-center border-bottom card-title">
            <h2 className="p-3">
              <FontAwesomeIcon icon={["fas", "earth-americas"]} className="me-2 text-primary" />
              {t('create_world')}
            </h2>
          </div>
          <div className="py-3 px-4">
            <div className="d-flex gap-2">
              <div className="img-sm flex-shrink-0">
                <SafeImg
                  className="img-fluid rounded-pill"
                  src={avatarUrl || placeholder_portrait_40x40}
                  alt="User Avatar"
                />
              </div>
              <div className="w-100">
                <div className={`p-2 border-0 rounded bg-light-gray post-edit-wrap mb-2 ${titleError ? 'border border-danger' : ''}`}>
                  <div className="position-relative">
                    <div
                      ref={titleRef}
                      contentEditable="true"
                      className="post-edit"
                      suppressContentEditableWarning={true}
                      placeholder={t('world_title')}
                      onPaste={pastePlainTextSingleLine}
                      onKeyDown={blockNewline}
                      onInput={handleTitleInput}
                    ></div>
                  </div>
                </div>
                {titleError && (
                  <small className="text-danger d-block mb-3">{t('in_words', { count: MAX_WORLD_TITLE })}</small>
                )}

                {/* 圖片獨立區塊 - 顯示在標題和內容之間 */}
                {images.length > 0 && (
                  <div className="mb-2">
                    <PostMediaEditor
                      images={images}
                      onDeleteImage={handleDeleteImage}
                      onEditMedia={handleEditMedia}
                      mode="create"
                      singleImageMode={true}
                    />
                  </div>
                )}

                <div className="p-2 border-0 rounded bg-light-gray post-edit-wrap world-content d-flex flex-column">
                  <div className="position-relative d-flex flex-column flex-grow-1">
                    {/* 文本 */}
                    <div
                      ref={contentEditableRef}
                      contentEditable="true"
                      className="post-edit flex-grow-1"
                      suppressContentEditableWarning={true}
                      placeholder={t('world_content')}
                      onPaste={pastePlainText}
                      onInput={handleContentInput}
                    ></div>
                  </div>
                </div>

                {/* 使用 MediaToolbar 組件 */}
                <MediaToolbar
                  onFileChange={handleFileChange}
                  onSubmit={createPost}
                  canSubmit={canPost}
                  isLoading={isLoading}
                  mode="create"
                  acceptFiles="image/png,image/jpg,image/jpeg"
                  multiple={false}
                  showVisibilityButton={false}
                  showScheduleButton={false}
                />
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* 成功訊息 */}
      <MessageModal ref={modalPostSuccess} status="success">
        <p>{t('world_success')}</p>
      </MessageModal>

      {/* 檔案大小錯誤 */}
      <MessageModal ref={modalSize} status="error">
        <p>{t('file_size_error')}</p>
      </MessageModal>

      {/* 檔案類型錯誤 */}
      <MessageModal ref={modalType} status="error">
        <p>{t('file_type_error')}</p>
      </MessageModal>

      {/* 圖片處理失敗 */}
      <MessageModal ref={modalImageProcessError} status="error">
        <p>{t('image_process_failed')}</p>
      </MessageModal>

      {/* API 非成功狀態碼統一由 StatusModal 顯示 */}
      <StatusModal ref={statusModalRef} statusCode={statusCode} />

      {/* 圖片編輯 Modal */}
      <EditMediaModal
        ref={editMediaModalRef}
        onSave={(_, caption) => {
          setImageCaption(caption);
        }}
      />

    </>
  );
};

export default MyWorldPost;